import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Download, Loader2, KeyRound, Fingerprint, Shield, Trash2, Plus } from "lucide-react";
import { getQuickDevice, setQuickDevice, clearQuickDevice, deviceLabel } from "@/lib/quickAccess";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function MyAccount() {
  const { user, can } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changing, setChanging] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Quick access state (solo admin)
  const isAdmin = user?.role === "admin";
  const [devices, setDevices] = useState([]);
  const [thisDevice, setThisDevice] = useState(() => getQuickDevice());
  const [qaDialogMode, setQaDialogMode] = useState(null); // 'register' | 'change'
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [qaSubmitting, setQaSubmitting] = useState(false);

  const reloadDevices = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/quick-access/devices");
      setDevices(r.data || []);
      // Verificar si el device local sigue en el listado activo
      const local = getQuickDevice();
      if (local && !r.data.find((d) => d.device_id === local.device_id)) {
        clearQuickDevice();
        setThisDevice(null);
      } else {
        setThisDevice(local);
      }
    } catch { /* silencioso */ }
  };
  useEffect(() => { reloadDevices(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error("Mínimo 8 caracteres");
    if (pw.next !== pw.confirm) return toast.error("No coinciden");
    setChanging(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Contraseña actualizada");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setChanging(false); }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/backup/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `power_cotizador_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No autorizado");
    } finally { setDownloading(false); }
  };

  // ---------- Quick Access actions ----------
  const openRegister = () => { setQaDialogMode("register"); setPin1(""); setPin2(""); setCurrentPin(""); };
  const openChange = () => { setQaDialogMode("change"); setPin1(""); setPin2(""); setCurrentPin(""); };

  const submitQa = async () => {
    if (!/^\d{4,6}$/.test(pin1)) return toast.error("PIN de 4 a 6 dígitos");
    if (pin1 !== pin2) return toast.error("Los PIN no coinciden");
    setQaSubmitting(true);
    try {
      if (qaDialogMode === "register") {
        const r = await api.post("/quick-access/register",
                                  { pin: pin1, device_label: deviceLabel() });
        setQuickDevice(r.data.device_id, r.data.first_name);
        setThisDevice({ device_id: r.data.device_id, first_name: r.data.first_name });
        toast.success("Acceso rápido activado en este dispositivo");
      } else {
        if (!thisDevice?.device_id) throw new Error("No hay dispositivo activo");
        await api.post("/quick-access/change-pin",
                        { device_id: thisDevice.device_id, current_pin: currentPin, new_pin: pin1 });
        toast.success("PIN actualizado");
      }
      setQaDialogMode(null);
      await reloadDevices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo completar");
    } finally { setQaSubmitting(false); }
  };

  const revokeDevice = async (device_id) => {
    if (!confirm("¿Revocar este dispositivo? El PIN dejará de servir de inmediato.")) return;
    try {
      await api.delete(`/quick-access/devices/${device_id}`);
      const local = getQuickDevice();
      if (local?.device_id === device_id) { clearQuickDevice(); setThisDevice(null); }
      toast.success("Dispositivo revocado");
      await reloadDevices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revocar");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-account-page">
      <div>
        <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Cuenta</div>
        <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight">Mi cuenta</h1>
      </div>

      <div className="pe-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre</div>
            <div className="font-semibold mt-1">{user?.name}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Email</div>
            <div className="font-semibold mt-1">{user?.email}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rol</div>
            <div className="font-semibold mt-1 capitalize">{user?.role}</div>
          </div>
        </div>
      </div>

      <form onSubmit={changePw} className="pe-card space-y-4" data-testid="change-pw-form">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-[#005A91]" />
          <h2 className="text-lg font-bold text-[#005A91]">Cambiar contraseña</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Actual</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" className="h-11 mt-1 border-[#005A91]/20" required />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nueva</Label>
            <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-next" className="h-11 mt-1 border-[#005A91]/20" required minLength={8} />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Confirmar</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className="h-11 mt-1 border-[#005A91]/20" required minLength={8} />
          </div>
        </div>
        <Button type="submit" disabled={changing} data-testid="change-pw-btn" className="bg-[#2E75B5] hover:bg-[#3a88cd] font-bold">
          {changing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Actualizar contraseña
        </Button>
      </form>

      {isAdmin && (
        <div className="pe-card space-y-4" data-testid="qa-section">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-[#005A91]" />
              <h2 className="text-lg font-bold text-[#005A91]">Acceso rápido</h2>
            </div>
            {thisDevice ? (
              <Button variant="outline" onClick={openChange} data-testid="qa-change-pin-btn"
                      className="border-[#005A91]/20 text-[#005A91]">
                <KeyRound className="h-4 w-4 mr-1.5" /> Cambiar PIN
              </Button>
            ) : (
              <Button onClick={openRegister} data-testid="qa-activate-btn"
                      className="bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
                <Plus className="h-4 w-4 mr-1.5" /> Activar en este dispositivo
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-600">
            Con un PIN de 4 a 6 dígitos entras más rápido. Solo aplica al rol Administrador y
            expira a los 7 días de inactividad. 5 intentos fallidos → PIN bloqueado, se pide
            contraseña completa. Todo movimiento queda registrado en auditoría.
          </p>

          {devices.length > 0 && (
            <div className="border-t border-slate-200 pt-4">
              <div className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500 mb-3">
                Dispositivos confiados ({devices.length})
              </div>
              <div className="space-y-2">
                {devices.map((d) => (
                  <div key={d.device_id} data-testid={`qa-device-${d.device_id}`}
                       className="flex items-start justify-between gap-3 bg-slate-50 rounded-md p-3 border border-slate-200">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Shield className={`h-4 w-4 shrink-0 ${d.locked ? "text-red-500" : d.expired ? "text-amber-500" : "text-emerald-500"}`} />
                        <div className="text-sm font-bold truncate">
                          {d.device_label || "Dispositivo"}
                        </div>
                        {thisDevice?.device_id === d.device_id && (
                          <span className="text-[10px] font-black tracking-wider uppercase bg-[#4ECCC8]/20 text-[#005A91] px-2 py-0.5 rounded">Este</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span>Último uso: {d.last_used_at ? new Date(d.last_used_at).toLocaleString("es-MX") : "—"}</span>
                        <span>Expira: {d.expires_at ? new Date(d.expires_at).toLocaleDateString("es-MX") : "—"}</span>
                        {d.locked && <span className="text-red-600 font-bold">PIN BLOQUEADO</span>}
                        {d.expired && !d.locked && <span className="text-amber-600 font-bold">EXPIRADO</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revokeDevice(d.device_id)}
                            data-testid={`qa-revoke-${d.device_id}`}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {can("backup") && (
        <div className="pe-card">
          <div className="flex items-center gap-2 mb-2">
            <Download className="h-5 w-5 text-[#005A91]" />
            <h2 className="text-lg font-bold text-[#005A91]">Respaldo completo</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Descarga un archivo ZIP con toda la base de datos (JSON + CSV por colección). Se registra en el log de auditoría.
          </p>
          <Button onClick={downloadBackup} disabled={downloading} data-testid="download-backup-btn" className="bg-[#005A91] hover:bg-[#004a7a] font-bold">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Descargar respaldo completo
          </Button>
        </div>
      )}

      {/* Quick Access dialog (register / change-pin) */}
      <Dialog open={!!qaDialogMode} onOpenChange={(o) => !o && setQaDialogMode(null)}>
        <DialogContent data-testid="qa-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#005A91] font-black text-xl">
              {qaDialogMode === "register" ? "Activar acceso rápido" : "Cambiar PIN"}
            </DialogTitle>
            <DialogDescription>
              {qaDialogMode === "register"
                ? "Escribe un PIN de 4 a 6 dígitos. Se guarda encriptado en el servidor y solo desbloquea en este dispositivo."
                : "Para cambiar tu PIN necesitamos el actual y luego el nuevo (2 veces)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {qaDialogMode === "change" && (
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">PIN actual</Label>
                <Input type="password" inputMode="numeric" maxLength={6} value={currentPin}
                       onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       data-testid="qa-current-pin" className="h-11 mt-1 border-[#005A91]/20 text-center text-xl pe-numeric tracking-[0.4em]" />
              </div>
            )}
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {qaDialogMode === "register" ? "PIN (4-6 dígitos)" : "PIN nuevo"}
              </Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={pin1}
                     onChange={(e) => setPin1(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     data-testid="qa-pin1" autoFocus
                     className="h-11 mt-1 border-[#005A91]/20 text-center text-xl pe-numeric tracking-[0.4em]" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Confirmar</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={pin2}
                     onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     data-testid="qa-pin2"
                     className="h-11 mt-1 border-[#005A91]/20 text-center text-xl pe-numeric tracking-[0.4em]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQaDialogMode(null)} disabled={qaSubmitting}>
              Cancelar
            </Button>
            <Button onClick={submitQa} disabled={qaSubmitting}
                    data-testid="qa-submit"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {qaSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {qaDialogMode === "register" ? "Activar" : "Guardar PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
